import { useState } from "react";
import { api, ApiError, type ItemAlmox } from "../api";
import { IconClose, IconCheck, IconTruck } from "../icons";

interface Props {
  item: ItemAlmox; // insumo a solicitar
  onClose: () => void;
  onSolicitada: () => void; // recarrega a lista de solicitações
}

/** Modal para solicitar ao Almoxarifado a separação de um insumo. */
export default function SolicitarInsumoModal({ item, onClose, onSolicitada }: Props) {
  const [quantidade, setQuantidade] = useState<string>("1");
  const [prioridade, setPrioridade] = useState<string>("Normal");
  const [pedido, setPedido] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  async function solicitar() {
    const qtd = Number(quantidade);
    if (!Number.isFinite(qtd) || qtd <= 0) {
      setError("Informe uma quantidade válida.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.almox.criarSolicitacao({
        item_id: item.id,
        quantidade: qtd,
        prioridade,
        pedido: pedido.trim(),
      });
      setOkMsg("Solicitação enviada ao Almoxarifado. Acompanhe o status em 'Minhas solicitações'.");
      onSolicitada();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao enviar a solicitação");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconTruck size={18} /> Solicitar separação
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="concluir-resumo">
            <strong>{item.descricao}</strong>
            <span>
              {item.codigo} • Disponível: {item.saldo} {item.unidade}
              {item.localizacao ? ` • ${item.localizacao}` : ""}
            </span>
          </div>

          <div className="form-grid">
            <label className="field">
              <span>Quantidade *</span>
              <input
                type="number"
                step="any"
                min="0"
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Prioridade</span>
              <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
                <option value="Normal">Normal</option>
                <option value="Alta">Alta</option>
                <option value="Urgente">Urgente</option>
              </select>
            </label>
            <label className="field field-wide">
              <span>Pedido / justificativa (opcional)</span>
              <textarea
                rows={3}
                value={pedido}
                placeholder="Ex.: para a obra X, atividade Y…"
                onChange={(e) => setPedido(e.target.value)}
              />
            </label>
          </div>

          {Number(quantidade) > item.saldo && (
            <p className="hint hint-warn">
              Atenção: a quantidade pedida ({quantidade}) é maior que o saldo disponível ({item.saldo}). O
              Almoxarifado pode separar parcialmente ou repor o estoque.
            </p>
          )}

          {error && <div className="form-error">{error}</div>}
          {okMsg && (
            <div className="form-ok">
              <IconCheck size={16} /> {okMsg}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {okMsg ? "Fechar" : "Cancelar"}
          </button>
          {!okMsg && (
            <button type="button" className="btn-primary" onClick={solicitar} disabled={saving}>
              {saving ? "Enviando…" : "Enviar solicitação"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
